import React, {useEffect, useRef, useState} from 'react';
import {Avatar} from '@lightricks/react-design-system';
import Aws from '@/services/aws/aws';
import raiseFlashMessage from '@/utils/raiseFlashMessage';
import translate from '@/utils/translate';
import {Brand} from '@/types/brand';
import useUpdateBrand from '@/hooks/mutations/use-update-brand';
import styles from './BrandAvatar.module.scss';

const MEGABYTE = 1048576;
const ACCEPTED_TYPES = ['image/gif', 'image/jpeg', 'image/png'];

interface BrandAvatarProps {
  brand: Brand;
  agencyId: string;
  size?: '3xl' | '2xl' | 'xl' | 'lg' | 'md' | 'sm' | 'xs' | '2xs' | '3xs';
}

function BrandAvatar({brand, agencyId, size = '2xl'}: BrandAvatarProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const updateBrand = useUpdateBrand(agencyId);

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const avatarUrl = brand.displayAvatarUrl?.includes('placeholder_avatar')
    ? null
    : brand.displayAvatarUrl;

  const backgroundUrl = previewUrl || avatarUrl;
  const initials = brand.displayName?.charAt(0)?.toUpperCase() || '?';

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!ACCEPTED_TYPES.includes(file.type)) {
      raiseFlashMessage({
        status: 'error',
        message: translate('Please select a GIF, JPEG, or PNG image.'),
      });
      return;
    }

    if (file.size > MEGABYTE) {
      raiseFlashMessage({
        status: 'error',
        message: translate('Image must be less than 1MB.'),
      });
      return;
    }

    setPreviewUrl(URL.createObjectURL(file));
    setIsUploading(true);

    let aws: Aws | undefined;
    try {
      aws = new Aws();
      const bucketName = Aws.s3Settings.buckets.contentSubmission || '';
      const s3Url = await aws.uploadToBucket(bucketName, {
        name: file.name,
        size: file.size,
        type: file.type,
        blob: file,
      });

      updateBrand.mutate(
        {
          brandId: brand.id,
          data: {avatarPictureAwsTempUrl: s3Url},
        },
        {
          onSuccess: () => {
            raiseFlashMessage({
              status: 'success',
              message: translate('Brand profile image updated successfully.'),
            });
            setIsUploading(false);
          },
          onError: () => {
            raiseFlashMessage({
              status: 'error',
              message: translate('Failed to update brand profile image.'),
            });
            setPreviewUrl(null);
            setIsUploading(false);
          },
        }
      );
    } catch {
      setPreviewUrl(null);
      setIsUploading(false);
      raiseFlashMessage({
        status: 'error',
        message: translate('Failed to update brand profile image.'),
      });
    } finally {
      aws?.dispose();
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <div
      className={styles.avatarWrapper}
      data-test-brand-editable-avatar={brand.id}
      onClick={() => fileInputRef.current?.click()}
    >
      <Avatar
        size={size}
        type={backgroundUrl ? 'picture' : 'initials'}
        src={backgroundUrl || undefined}
        initials={initials}
      />
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_TYPES.join(',')}
        className={styles.fileInput}
        onChange={handleFileSelect}
        data-test-brand-profile-picture-button
      />
      {isUploading && <div className={styles.uploading} />}
    </div>
  );
}

export default BrandAvatar;
